import React, { useState, useEffect } from 'react';
import CTASection from './CTASection';

const FAQSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getResponsiveMargin = () => {
    if (screenSize >= 1024) return '100px';
    if (screenSize >= 768) return '48px';
    return '20px';
  };

  const faqs = [
    {
      question: "How quickly can you bring my vision to life?",
      answer: "We work at the speed of innovation! Simple websites launch in 2-3 weeks, while complex digital experiences take 4-8 weeks. We'll give you a crystal-clear timeline upfront - no surprises, just results.",
      icon: "⚡"
    },
    {
      question: "What makes your development process different?",
      answer: "We're not just developers - we're digital architects. Every project gets our signature blend of cutting-edge tech, user-first design, and transparent communication. You'll see progress every step of the way.",
      icon: "🚀"
    },
    {
      question: "Will you support my project after launch?",
      answer: "Absolutely! We're your long-term digital partners. From security updates to feature enhancements, we've got your back 24/7. Your success is our success.",
      icon: "🛡️"
    },
    {
      question: "What technologies power your solutions?",
      answer: "We use the latest and greatest - React, Next.js, Node.js, Python, AWS, and more. Our tech stack evolves with the industry, ensuring your project stays ahead of the curve.",
      icon: "⚙️"
    },
    {
      question: "How do you guarantee premium quality?",
      answer: "Quality isn't just a promise - it's our obsession. Every line of code is tested, reviewed, and perfected. We deliver excellence, not just functionality.",
      icon: "💎"
    },
    {
      question: "Which industries do you revolutionize?",
      answer: "From fintech to healthcare, e-commerce to SaaS - we've transformed businesses across every sector. Your industry expertise + our technical mastery = unstoppable results.",
      icon: "🌟"
    }
  ];

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? -1 : index);
  };

  return (
    <div style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '3rem', paddingBottom: '3rem' }} className="mx-auto sm:px-6 lg:px-8 sm:py-16 lg:py-24">
      {/* FAQ Section */}
      <section style={{ 
        marginBottom: '5rem',
        padding: '2rem 1rem'
      }} className="sm:mb-28 sm:px-8 sm:py-12 lg:px-16 lg:py-20">
        <div style={{ textAlign: 'center', marginBottom: '5rem' }} className="mx-auto">
          <div style={{ paddingLeft: '0.75rem', paddingRight: '0.75rem', paddingTop: '0.5rem', paddingBottom: '0.5rem', marginBottom: '1.5rem' }} className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-full">
            <span className="text-xl sm:text-2xl">🤔</span>
            <span className="text-xs sm:text-sm font-medium" style={{color: '#182cd4'}}>Got Questions? We've Got Answers!</span>
          </div>
          <h2 style={{ marginBottom: '1rem', paddingLeft: '1rem', paddingRight: '1rem' }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 sm:mb-6 leading-tight">
            Everything You Need to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: 'linear-gradient(to right, #182cd4, #1a2eb8)'}}>
              Know
            </span>
          </h2>
          <p style={{ paddingLeft: '1rem', paddingRight: '1rem' }} className="text-lg sm:text-xl text-gray-600 leading-relaxed">
            Your success journey starts with clarity. Here are the answers to questions 
            <br className="hidden lg:block" />
            our clients ask most - because transparency builds trust.
          </p>
        </div>

        <div className="mx-auto space-y-3 sm:space-y-4" style={{ margin: getResponsiveMargin() }}>
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`group border-2 rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-2xl ${
                activeIndex === index 
                  ? 'border-transparent shadow-2xl transform scale-[1.01] sm:scale-[1.02]' 
                  : 'border-gray-100 hover:border-gray-200'
              }`}
              style={{
                background: activeIndex === index 
                  ? 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' 
                  : 'white',
                  marginTop: '20px'
              }}
            >
              <button
                style={{ padding: '1rem' }}
                className={`w-full sm:p-6 lg:p-8 text-left flex items-start sm:items-center justify-between transition-all duration-300 ${
                  activeIndex === index 
                    ? 'bg-transparent' 
                    : 'bg-white hover:bg-gray-50/50'
                }`}
                onClick={() => toggleFAQ(index)}
              >
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 lg:gap-6 flex-1 min-w-0">
                  <div 
                    className={`flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl sm:rounded-2xl text-lg sm:text-xl lg:text-2xl transition-all duration-300 flex-shrink-0 mt-1 sm:mt-0 ${
                      activeIndex === index 
                        ? 'shadow-lg transform scale-110' 
                        : 'group-hover:scale-105'
                    }`}
                    style={{
                      background: activeIndex === index 
                        ? 'linear-gradient(135deg, #182cd4, #1a2eb8)' 
                        : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)'
                    }}
                  >
                    <span className={`transition-all duration-300 ${
                      activeIndex === index ? 'grayscale-0' : 'grayscale'
                    }`}>
                      {faq.icon}
                    </span>
                  </div>
                  <h3 className={`text-lg sm:text-xl lg:text-2xl font-bold transition-colors duration-300 text-left leading-tight ${
                    activeIndex === index ? 'text-gray-900' : 'text-gray-800 group-hover:text-gray-900'
                  }`}>
                    {faq.question}
                  </h3>
                </div>
                <div 
                  className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl transition-all duration-300 flex-shrink-0 ml-2 sm:ml-4 mt-1 sm:mt-0 ${
                    activeIndex === index 
                      ? 'transform rotate-180 shadow-md' 
                      : 'group-hover:bg-gray-100'
                  }`}
                  style={{
                    background: activeIndex === index ? '#182cd4' : 'transparent'
                  }}
                >
                  <svg 
                    className={`w-5 h-5 sm:w-6 sm:h-6 transition-all duration-300 ${
                      activeIndex === index ? 'text-white' : 'text-gray-500'
                    }`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              <div 
                className={`overflow-hidden transition-all duration-700 ease-out ${
                  activeIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }} className="sm:px-6 lg:px-8 sm:pb-6 lg:pb-8">
                  <div style={{ paddingLeft: '0' }} className="sm:pl-18 lg:pl-22">
                    <div 
                      style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.8)' }}
                      className="sm:p-5 lg:p-6 rounded-xl sm:rounded-2xl text-gray-700 text-base sm:text-lg leading-relaxed text-left"
                    >
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div style={{ textAlign: 'center', marginTop: '3rem', paddingLeft: '1rem', paddingRight: '1rem' }} className="sm:mt-16">
          <div style={{ marginBottom: '1rem' }} className="inline-flex items-center gap-2 sm:gap-3 text-gray-600 sm:mb-6 text-sm sm:text-base">
            <span>Still have questions?</span>
            <span className="text-xl sm:text-2xl">💬</span>
          </div>
          <a
            href="/contact"
            style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', marginLeft: '0.5rem', marginRight: '0.5rem', background: 'linear-gradient(135deg, #182cd4, #1a2eb8)' }}
            className="inline-flex items-center gap-2 sm:gap-3 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl text-white font-semibold text-base sm:text-lg transition-all duration-300 hover:shadow-xl hover:transform hover:scale-105"
          >
            <span>Let's Chat</span>
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <div style={{ margin: getResponsiveMargin() }}>

      <CTASection />
      </div>
    </div>
  );
};

export default FAQSection;